import { Text } from '@components'
import { type ChannelReads, api } from '@here/api'
import { useService } from 'solid-services'
import { useQuery } from '@tanstack/solid-query'
import { createMemo } from 'solid-js'
import { css } from 'styled-system/css'
import { Circle } from 'styled-system/jsx'
import { useChannelsReads } from '../../state/channelReads/state'

async function fetchMessageCount(
	channelId: string,
	lastReadAt?: string,
): Promise<string> {
	const sb = api()
	const q = sb
		.from('message')
		.select('id', { count: 'exact', head: true })
		.eq('channel_id', channelId)

	if (lastReadAt) q.gt('created_at', lastReadAt)

	const { count, error } = await q
	if (error || count == null) {
		console.error('Failed to count unread messages:', error)
		return '0'
	}
	return count < 100 ? String(count) : '99+'
}

export const UnreadCount = (props: { lastReadAt?: string; channelId?: string }) => {
	const { channelReadDictionary } = useService(useChannelsReads)()

	// 👇 derive a single scalar for the key + query
	const effectiveLastReadAt = createMemo(() => {
		const dict = channelReadDictionary()
		const fromDict = props.channelId
			? dict?.[props.channelId!]?.last_read_at
			: undefined
		return fromDict ?? props.lastReadAt // prefer service, fall back to prop
	})

	const q = useQuery(() => ({
		// 👇 include the derived scalar in the key so changes auto-refetch
		queryKey: ['message-count', props.channelId, effectiveLastReadAt()],
		queryFn: () =>
			props.channelId
				? fetchMessageCount(props.channelId, effectiveLastReadAt())
				: Promise.resolve(' '),
		enabled: !!props.channelId,
		staleTime: 60 * 1000,
		gcTime: 60 * 1000,
		retry: 1,
		placeholderData: '-',
	}))

	return (
		<Circle
			class={css({
				background: 'error',
				h: 5,
				w: 5,
			})}
			title={q.data ?? ''}
			data-unread={q.data ?? ''}
		>
			<Text
				fontSize="10px"
				color="white"
			>
				{q.data}
			</Text>
		</Circle>
	)
}
